import React, { useState, useEffect } from 'react';
import { useLocation } from '../contexts/LocationContext';
import { apiClient } from '../utils/api';
import CreateCycleForm from '../components/CreateCycleForm';
import { 
  CalendarIcon, 
  PlusIcon, 
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  CheckIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

interface Cycle {
  id: string;
  pond_id: string;
  pond_name: string;
  pond_type: string;
  cycle_name: string;
  planned_start_date: string;
  realistic_harvest_date: string;
  optimistic_harvest_date: string;
  pessimistic_harvest_date: string;
  status: string;
  growth_health_status: string;
  fish_count: number;
  current_weight_g: number;
  target_weight_g: number;
  projected_revenue: number;
  fish_species: string;
}

interface CycleTimelineItem {
  cycle_id: string;
  pond_id: string;
  pond_name: string;
  pond_type: string;
  cycle_name: string;
  start_date: string;
  end_date: string;
  status: string;
  growth_health_status: string;
  fish_count?: number;
  current_weight_g?: number;
  target_weight_g: number;
}

interface CycleScenario {
  scenario_type: string;
  cycles_count: number;
  total_revenue: number;
  total_seed_required: number;
  harvest_dates: string[];
  avg_harvest_weight_g: number;
  seed_requirement: number;
  risk_level: string;
  description: string;
  recommended_cycle?: boolean;
}

interface ActionableRecommendation {
  action_type: string;
  priority: string;
  source_pond_id?: string;
  target_pond_id?: string;
  source_cycle_id?: string;
  target_cycle_id?: string;
  fish_count: number;
  species: string;
  avg_weight_g?: number;
  recommended_date: string;
  reason: string;
}

interface SmartCycleScenario {
  scenario_type: string;
  is_feasible: boolean;
  feasibility_reason: string;
  total_revenue: number;
  total_seed_required: number;
  harvest_dates: string[];
  recommendations: ActionableRecommendation[];
  cycles_count: number;
  avg_harvest_weight_g: number;
  risk_level: string;
  description: string;
}

const CyclePlanning: React.FC = () => {
  const { selectedLocation } = useLocation();
  
  const [activeTab, setActiveTab] = useState<'timeline' | 'scenarios' | 'transfers' | 'analytics'>('timeline');
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [timelineData, setTimelineData] = useState<CycleTimelineItem[]>([]);
  const [scenarios, setScenarios] = useState<CycleScenario[]>([]);
  const [smartScenarios, setSmartScenarios] = useState<SmartCycleScenario[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [loadingTransfers, setLoadingTransfers] = useState(false);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [dataLoaded, setDataLoaded] = useState({
    timeline: false,
    scenarios: false,
    transfers: false,
    analytics: false
  });
  const [seasonYear, setSeasonYear] = useState(new Date().getFullYear());
  const [showCreateCycle, setShowCreateCycle] = useState(false);
  const [useSmartScenarios, setUseSmartScenarios] = useState(true);
  const [expandedScenarios, setExpandedScenarios] = useState<Set<string>>(new Set());
  const [acceptingTransfers, setAcceptingTransfers] = useState<Set<string>>(new Set());
  const [acceptedRecommendations, setAcceptedRecommendations] = useState<Set<string>>(new Set());
  const [transfers, setTransfers] = useState<any[]>([]);
  const [ponds, setPonds] = useState<any[]>([]);
  const [showCreateTransferModal, setShowCreateTransferModal] = useState(false);
  const [fishSpecies, setFishSpecies] = useState<any[]>([]);
  const [selectedSourcePond, setSelectedSourcePond] = useState<string>('');
  const [selectedSpecies, setSelectedSpecies] = useState<string>('');
  const [sourcePondSpeciesInventory, setSourcePondSpeciesInventory] = useState<any[]>([]);
  const [loadingSpeciesInventory, setLoadingSpeciesInventory] = useState(false);
  const [editingTransfer, setEditingTransfer] = useState<any>(null);
  const [showEditTransferModal, setShowEditTransferModal] = useState(false);

  const formatCurrency = (amount: number) => {
    if (amount >= 10000000) { // 1 crore or more
      return `₹${(amount / 10000000).toFixed(1)}Cr`;
    } else if (amount >= 100000) { // 1 lakh or more
      return `₹${(amount / 100000).toFixed(1)}L`;
    } else if (amount >= 1000) { // 1 thousand or more
      return `₹${(amount / 1000).toFixed(1)}K`;
    } else {
      return `₹${amount.toFixed(0)}`;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planned': return 'bg-blue-100 text-blue-800';
      case 'stocked': return 'bg-green-100 text-green-800';
      case 'growing': return 'bg-yellow-100 text-yellow-800';
      case 'ready': return 'bg-orange-100 text-orange-800';
      case 'harvested': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'on_track': return 'text-green-600';
      case 'ahead': return 'text-blue-600';
      case 'behind': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getHealthStatusIcon = (status: string) => {
    switch (status) {
      case 'on_track': return <ArrowTrendingUpIcon className="h-4 w-4" />;
      case 'ahead': return <ArrowTrendingUpIcon className="h-4 w-4" />;
      case 'behind': return <ExclamationTriangleIcon className="h-4 w-4" />;
      default: return <ClockIcon className="h-4 w-4" />;
    }
  };

  const fetchCycles = async () => {
    if (!selectedLocation) return;
    
    setLoading(true);
    try {
      const response = await apiClient.get(`/cycles?location_id=${selectedLocation.id}&season_year=${seasonYear}`);
      setCycles(response.data);
    } catch (err) {
      console.error('Error fetching cycles:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeline = async () => {
    if (!selectedLocation) return;
    
    const startDate = new Date(seasonYear, 0, 1).toISOString().split('T')[0]; // Jan 1
    const endDate = new Date(seasonYear, 11, 31).toISOString().split('T')[0]; // Dec 31
    
    try {
      const response = await apiClient.get(`/cycles/timeline?location_id=${selectedLocation.id}&start_date=${startDate}&end_date=${endDate}`);
      setTimelineData(response.data);
    } catch (err) {
      console.error('Error fetching timeline:', err);
    }
  };

  const fetchScenarios = async () => {
    if (!selectedLocation) return;
    
    try {
      const response = await apiClient.get(`/cycles/scenarios?location_id=${selectedLocation.id}&season_year=${seasonYear}`);
      setScenarios(response.data);
    } catch (err) {
      console.error('Error fetching scenarios:', err);
    }
  };

  const fetchSmartScenarios = async () => {
    if (!selectedLocation) return;
    
    try {
      const response = await apiClient.get(`/cycles/scenarios/smart?location_id=${selectedLocation.id}&season_year=${seasonYear}`);
      setSmartScenarios(response.data);
    } catch (err) {
      console.error('Error fetching smart scenarios:', err);
    }
  };

  const fetchTransfers = async () => {
    if (!selectedLocation) return;
    
    try {
      setLoadingTransfers(true);
      const response = await apiClient.get(`/transfers?location_id=${selectedLocation.id}`);
      // Sort transfers by creation date (newest first)
      const sortedTransfers = response.data.sort((a: any, b: any) => {
        const dateA = new Date(a.created_at || a.transfer_date || 0);
        const dateB = new Date(b.created_at || b.transfer_date || 0);
        return dateB.getTime() - dateA.getTime(); // Descending order (newest first)
      });
      setTransfers(sortedTransfers);
    } catch (error) {
      console.error('Error fetching transfers:', error);
    } finally {
      setLoadingTransfers(false);
    }
  };

  const fetchPonds = async () => {
    if (!selectedLocation) return;
    
    try {
      // Use the proper ponds API endpoint
      const response = await apiClient.get(`/locations/${selectedLocation.id}/ponds`);
      const ponds = response.data;
      setPonds(ponds);
    } catch (error) {
      console.error('Error fetching ponds:', error);
    }
  };

  const getPondName = (pondId: string) => {
    const pond = ponds.find(p => p.id === pondId);
    return pond ? pond.name : pondId.slice(0, 8) + '...';
  };

  const fetchFishSpecies = async () => {
    if (!selectedLocation?.id) {
      setFishSpecies([]);
      return;
    }
    try {
      const response = await apiClient.get('/master-data/fish_species', {
        params: { location_id: selectedLocation.id }
      });
      setFishSpecies(response.data);
    } catch (error) {
      console.error('Error fetching fish species:', error);
    }
  };

  const fetchSourcePondSpeciesInventory = async (pondId: string) => {
    if (!pondId) {
      setSourcePondSpeciesInventory([]);
      return;
    }

    setLoadingSpeciesInventory(true);
    try {
      const response = await apiClient.get(`/ponds/${pondId}/species-inventory`);
      setSourcePondSpeciesInventory(response.data);
    } catch (error) {
      console.error('Error fetching species inventory:', error);
      setSourcePondSpeciesInventory([]);
    } finally {
      setLoadingSpeciesInventory(false);
    }
  };

  const handleSourcePondChange = (pondId: string) => {
    setSelectedSourcePond(pondId);
    setSelectedSpecies('');
    fetchSourcePondSpeciesInventory(pondId);
  };

  const handleSpeciesChange = (species: string) => {
    setSelectedSpecies(species);
  };

  const getAvailableCount = (species: string) => {
    const speciesData = getNormalizedSpeciesInventory().find(s => s.species === species);
    return speciesData ? speciesData.count : 0;
  };

  const getAverageWeight = (species: string) => {
    const speciesData = getNormalizedSpeciesInventory().find(s => s.species === species);
    return speciesData ? speciesData.avg_weight_g : 0;
  };

  // Normalize species names to prevent duplicates
  const normalizeSpeciesName = (species: string): string => {
    const normalized = species.toLowerCase().trim();
    const masterDataMapping: Record<string, string> = {
      'rohu': 'Rohu',
      'catla': 'Catla',
      'mrigal': 'Mrigal',
      'grass carp': 'Grass Carp',
      'silver carp': 'Silver Carp',
      'bighead carp': 'Bighead Carp',
      'tilapia': 'Tilapia',
      'pangash': 'Pangash',
      'pangasius': 'Pangasius',
      'common carp': 'Common Carp'
    };
    return masterDataMapping[normalized] || species;
  };

  // Get normalized species inventory without duplicates
  const getNormalizedSpeciesInventory = () => {
    const normalizedSpecies: Record<string, any> = {};

    sourcePondSpeciesInventory.forEach(species => {
      const normalizedName = normalizeSpeciesName(species.species);

      if (normalizedName in normalizedSpecies) {
        // Merge with existing species
        const existing = normalizedSpecies[normalizedName];
        normalizedSpecies[normalizedName] = {
          species: normalizedName,
          count: (existing.count || 0) + (species.count || 0),
          biomass_kg: (existing.biomass_kg || 0) + (species.biomass_kg || 0),
          avg_weight_g: existing.avg_weight_g || species.avg_weight_g || 0,
          last_updated: existing.last_updated || species.last_updated
        };
      } else {
        // New species
        normalizedSpecies[normalizedName] = {
          species: normalizedName,
          count: species.count || 0,
          biomass_kg: species.biomass_kg || 0,
          avg_weight_g: species.avg_weight_g || 0,
          last_updated: species.last_updated
        };
      }
    });

    return Object.values(normalizedSpecies).filter(s => (s.count || 0) > 0 || (s.biomass_kg || 0) > 0);
  };

  useEffect(() => {
    if (selectedLocation) {
      // Only load timeline data initially
      fetchCycles();
      fetchTimeline();
      fetchPonds();
      fetchFishSpecies();
      setDataLoaded(prev => ({ ...prev, timeline: true }));
    }
  }, [selectedLocation, seasonYear]);

  const handleTabClick = async (tabId: string) => {
    setActiveTab(tabId as any);
    
    // Load data for the clicked tab if not already loaded
    if (!dataLoaded[tabId as keyof typeof dataLoaded]) {
      switch (tabId) {
        case 'scenarios':
          setLoadingScenarios(true);
          try {
            await Promise.all([fetchScenarios(), fetchSmartScenarios()]);
            setDataLoaded(prev => ({ ...prev, scenarios: true }));
          } finally {
            setLoadingScenarios(false);
          }
          break;
        case 'transfers':
          setLoadingTransfers(true);
          try {
            await fetchTransfers();
            setDataLoaded(prev => ({ ...prev, transfers: true }));
          } finally {
            setLoadingTransfers(false);
          }
          break;
        case 'analytics':
          setLoadingAnalytics(true);
          try {
            // Analytics data loading logic here
            setDataLoaded(prev => ({ ...prev, analytics: true }));
          } finally {
            setLoadingAnalytics(false);
          }
          break;
      }
    }
  };

  const handleCycleCreated = () => {
    // Refresh timeline data when a new cycle is created
    fetchCycles();
    fetchTimeline();
    // Reset data loaded flags for other tabs since data might have changed
    setDataLoaded(prev => ({ ...prev, scenarios: false, transfers: false }));
  };

  const toggleScenarioExpansion = (scenarioType: string) => {
    const newExpanded = new Set(expandedScenarios);
    if (newExpanded.has(scenarioType)) {
      newExpanded.delete(scenarioType);
    } else {
      newExpanded.add(scenarioType);
    }
    setExpandedScenarios(newExpanded);
  };

  const acceptTransfer = async (recommendation: ActionableRecommendation) => {
    const transferKey = `${recommendation.source_pond_id}-${recommendation.target_pond_id}-${recommendation.recommended_date}`;
    
    try {
      setAcceptingTransfers(prev => new Set(prev).add(transferKey));
      
      // Derive avg weight if missing from recommendation
      const deriveAvgWeightFromReason = (reason?: string): number | undefined => {
        if (!reason) return undefined;
        // Match patterns like "95g avg" or "(95g avg)"
        const match = reason.match(/(\d+(?:\.\d+)?)\s*g\s*avg/i);
        if (match && match[1]) {
          const val = parseFloat(match[1]);
          return isNaN(val) ? undefined : val;
        }
        return undefined;
      };

      const findSourceCycleAvg = (): number | undefined => {
        try {
          const cycle = cycles.find(c => c.pond_id === recommendation.source_pond_id);
          if (cycle && typeof cycle.current_weight_g === 'number' && cycle.current_weight_g > 0) {
            return Math.round(cycle.current_weight_g);
          }
        } catch {}
        return undefined;
      };

      const computedAvgWeight = Math.round(
        (typeof recommendation.avg_weight_g === 'number' && recommendation.avg_weight_g > 0)
          ? recommendation.avg_weight_g
          : (findSourceCycleAvg() ?? deriveAvgWeightFromReason(recommendation.reason) ?? 0)
      );

      const transferData = {
        source_pond_id: recommendation.source_pond_id,
        target_pond_id: recommendation.target_pond_id,
        source_cycle_id: recommendation.source_cycle_id || null,
        target_cycle_id: recommendation.target_cycle_id || null,
        fish_count: recommendation.fish_count,
        species: recommendation.species,
        avg_weight_g: computedAvgWeight,
        transfer_type: recommendation.action_type === 'transfer_nursery_to_grower' ? 'nursery_to_grower' : 'grower_to_grower',
        planned_date: recommendation.recommended_date,
        reason: recommendation.reason
      };

      const response = await apiClient.post(`/transfers?location_id=${selectedLocation?.id}`, transferData);
      
      if (response.status === 200) {
        alert(`✅ Transfer created successfully!\n\nTransfer ID: ${response.data.id}\nStatus: Open (Pending Physical Completion)`);
        // Mark recommendation as accepted
        setAcceptedRecommendations(prev => new Set(prev).add(transferKey));
        // Refresh scenarios to show updated state
        fetchSmartScenarios();
        fetchTransfers();
      }
    } catch (error) {
      console.error('Error creating transfer:', error);
      alert('❌ Failed to create transfer. Please try again.');
    } finally {
      setAcceptingTransfers(prev => {
        const newSet = new Set(prev);
        newSet.delete(transferKey);
        return newSet;
      });
    }
  };

  const completeTransfer = async (transferId: string) => {
    try {
      await apiClient.post(`/transfers/${transferId}/complete`);
      alert('✅ Transfer marked as completed!');
      fetchTransfers();
      fetchCycles(); // Refresh cycles to show updated fish counts
    } catch (error) {
      console.error('Error completing transfer:', error);
      alert('❌ Failed to complete transfer. Please try again.');
    }
  };

  const cancelTransfer = async (transferId: string) => {
    if (!window.confirm('Are you sure you want to cancel this transfer?')) return;
    
    try {
      const reason = 'Cancelled from UI';
      await apiClient.post(`/transfers/${transferId}/cancel?reason=${encodeURIComponent(reason)}`);
      alert('✅ Transfer cancelled successfully!');
      fetchTransfers();
    } catch (error) {
      console.error('Error cancelling transfer:', error);
      alert('❌ Failed to cancel transfer. Please try again.');
    }
  };

  const editTransfer = (transfer: any) => {
    setEditingTransfer(transfer);
    setSelectedSourcePond(transfer.source_pond_id);
    setSelectedSpecies(transfer.species);
    fetchSourcePondSpeciesInventory(transfer.source_pond_id);
    setShowEditTransferModal(true);
  };

  const deleteTransfer = async (transferId: string) => {
    if (!window.confirm('Are you sure you want to delete this transfer? This action cannot be undone.')) return;
    
    try {
      await apiClient.delete(`/transfers/${transferId}`);
      alert('✅ Transfer deleted successfully!');
      fetchTransfers();
    } catch (error) {
      console.error('Error deleting transfer:', error);
      alert('❌ Failed to delete transfer. Please try again.');
    }
  };

  const activeCycles = cycles.filter(cycle => ['planned', 'stocked', 'growing'].includes(cycle.status));
  const totalProjectedRevenue = cycles.reduce((sum, cycle) => sum + (cycle.projected_revenue || 0), 0);
  const upcomingHarvests = cycles.filter(cycle => {
    const harvestDate = new Date(cycle.realistic_harvest_date);
    const today = new Date();
    const daysUntilHarvest = Math.ceil((harvestDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilHarvest <= 30 && daysUntilHarvest >= 0;
  });

  if (!selectedLocation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Location Selected</h3>
          <p className="text-gray-500">Please select a location to view cycle planning</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Cycle Planning & Forecasting</h1>
              <p className="text-gray-600 mt-1">
                {selectedLocation.name} • Season {seasonYear}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <select
                value={seasonYear}
                onChange={(e) => setSeasonYear(parseInt(e.target.value))}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value={2024}>2024</option>
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
              </select>
              <button
                onClick={() => setShowCreateCycle(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Cycle
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{activeCycles.length}</div>
              <div className="text-sm text-gray-600">Active Cycles</div>
              <div className="text-xs text-gray-500">Currently running</div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(totalProjectedRevenue)}
              </div>
              <div className="text-sm text-gray-600">Projected Revenue</div>
              <div className="text-xs text-gray-500">Season {seasonYear}</div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">{upcomingHarvests.length}</div>
              <div className="text-sm text-gray-600">Upcoming Harvests</div>
              <div className="text-xs text-gray-500">Next 30 days</div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {cycles.filter(c => c.growth_health_status === 'on_track').length}
              </div>
              <div className="text-sm text-gray-600">On Track</div>
              <div className="text-xs text-gray-500">Growth health</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'timeline', name: 'Timeline', icon: CalendarIcon },
                { id: 'scenarios', name: 'Scenarios', icon: ChartBarIcon },
                { id: 'transfers', name: 'Transfers', icon: ArrowTrendingUpIcon },
                { id: 'analytics', name: 'Analytics', icon: ChartBarIcon }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <tab.icon className="h-4 w-4 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'timeline' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Cycle Timeline</h3>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Loading timeline...</p>
                </div>
              ) : cycles.length === 0 ? (
                <div className="text-center py-8">
                  <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Cycles Found</h3>
                  <p className="text-gray-500">Create your first cycle to get started</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {cycles.map((cycle) => (
                    <div key={cycle.id} className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm">
                      {/* Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-4">
                          <div className="flex-shrink-0">
                            <div className={`w-4 h-4 rounded-full ${
                              cycle.pond_type === 'nursery' ? 'bg-blue-500' : 'bg-green-500'
                            }`}></div>
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900">{cycle.cycle_name}</h4>
                            <p className="text-sm text-gray-600">{cycle.pond_name} ({cycle.pond_type}) • {cycle.fish_species}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className={`flex items-center space-x-1 ${getHealthStatusColor(cycle.growth_health_status)}`}>
                            {getHealthStatusIcon(cycle.growth_health_status)}
                            <span className="text-sm font-medium capitalize">
                              {cycle.growth_health_status.replace('_', ' ')}
                            </span>
                          </div>
                          <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(cycle.status)}`}>
                            {cycle.status}
                          </span>
                        </div>
                      </div>

                      {/* Key Metrics Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="text-xs text-gray-500 uppercase tracking-wide">Fish Count</div>
                          <div className="text-lg font-semibold text-gray-900">
                            {cycle.fish_count ? cycle.fish_count.toLocaleString() : 'No data'}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="text-xs text-gray-500 uppercase tracking-wide">Current Weight</div>
                          <div className="text-lg font-semibold text-gray-900">
                            {cycle.current_weight_g ? `${Math.round(cycle.current_weight_g)}g` : 'Unknown'}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="text-xs text-gray-500 uppercase tracking-wide">Target Weight</div>
                          <div className="text-lg font-semibold text-gray-900">
                            {cycle.target_weight_g ? `${cycle.target_weight_g}g` : 'Unknown'}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <div className="text-xs text-gray-500 uppercase tracking-wide">Projected Revenue</div>
                          <div className="text-lg font-semibold text-green-600">
                            {cycle.projected_revenue ? formatCurrency(cycle.projected_revenue) : 'Unknown'}
                          </div>
                        </div>
                      </div>

                      {/* Harvest Timeline */}
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h5 className="text-sm font-semibold text-blue-900 mb-3">Harvest Timeline</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="text-center">
                            <div className="text-xs text-blue-600 uppercase tracking-wide mb-1">Optimistic</div>
                            <div className="text-sm font-semibold text-blue-900">
                              {cycle.optimistic_harvest_date ? formatDate(cycle.optimistic_harvest_date) : 'Unknown'}
                            </div>
                            <div className="text-xs text-blue-600">Best case scenario</div>
                          </div>
                          <div className="text-center border-l border-r border-blue-200">
                            <div className="text-xs text-blue-600 uppercase tracking-wide mb-1">Realistic</div>
                            <div className="text-sm font-semibold text-blue-900">
                              {cycle.realistic_harvest_date ? formatDate(cycle.realistic_harvest_date) : 'Unknown'}
                            </div>
                            <div className="text-xs text-blue-600">Most likely</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xs text-blue-600 uppercase tracking-wide mb-1">Pessimistic</div>
                            <div className="text-sm font-semibold text-blue-900">
                              {cycle.pessimistic_harvest_date ? formatDate(cycle.pessimistic_harvest_date) : 'Unknown'}
                            </div>
                            <div className="text-xs text-blue-600">Worst case scenario</div>
                          </div>
                        </div>
                      </div>

                      {/* Growth Progress */}
                      <div className="mt-4">
                        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                          <span>Growth Progress</span>
                          <span>
                            {cycle.current_weight_g && cycle.target_weight_g 
                              ? `${Math.round((cycle.current_weight_g / cycle.target_weight_g) * 100)}%`
                              : 'Unknown'
                            }
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-500 h-2 rounded-full transition-all duration-300"
                            style={{
                              width: cycle.current_weight_g && cycle.target_weight_g 
                                ? `${Math.min((cycle.current_weight_g / cycle.target_weight_g) * 100, 100)}%`
                                : '0%'
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'scenarios' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Cycle Scenarios</h3>
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="smart-scenarios"
                      checked={useSmartScenarios}
                      onChange={(e) => setUseSmartScenarios(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="smart-scenarios" className="text-sm font-medium text-gray-700">
                      Smart Analysis
                    </label>
                  </div>
                  <button
                    onClick={() => {
                      if (useSmartScenarios) {
                        fetchSmartScenarios();
                      } else {
                        fetchScenarios();
                      }
                    }}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Refresh
                  </button>
                </div>
              </div>
              
              {loadingScenarios ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Loading scenarios...</p>
                </div>
              ) : useSmartScenarios ? (
                // Smart Scenarios
                smartScenarios.length === 0 ? (
                  <div className="text-center py-8">
                    <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Smart Scenarios Available</h3>
                    <p className="text-gray-500">Create cycles and ponds to see intelligent scenario analysis</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {smartScenarios.map((scenario) => (
                      <div key={scenario.scenario_type} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <h4 className="text-lg font-semibold text-gray-900 capitalize">
                              {scenario.scenario_type.replace('-', ' ')}
                            </h4>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              scenario.is_feasible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {scenario.is_feasible ? '✅ Feasible' : '❌ Not Feasible'}
                            </span>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              scenario.risk_level === 'low' ? 'bg-green-100 text-green-800' :
                              scenario.risk_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {scenario.risk_level} risk
                            </span>
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <p className="text-sm text-gray-600 mb-2">
                            <strong>Analysis:</strong> {scenario.feasibility_reason}
                          </p>
                          <p className="text-sm text-gray-500">{scenario.description}</p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="text-sm text-gray-600">Projected Revenue</div>
                            <div className="text-lg font-semibold text-gray-900">
                              {formatCurrency(scenario.total_revenue)}
                            </div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="text-sm text-gray-600">Fish Count</div>
                            <div className="text-lg font-semibold text-gray-900">
                              {scenario.total_seed_required.toLocaleString()}
                            </div>
                          </div>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="text-sm text-gray-600">Avg Weight</div>
                            <div className="text-lg font-semibold text-gray-900">
                              {scenario.avg_harvest_weight_g}g
                            </div>
                          </div>
                        </div>

                        {/* Actionable Recommendations */}
                        {scenario.recommendations.length > 0 && (
                          <div className="border-t border-gray-200 pt-4">
                            <h5 className="text-sm font-semibold text-gray-900 mb-3">
                              📋 Actionable Recommendations ({scenario.recommendations.length})
                            </h5>
                            <div className="space-y-2">
                              {(expandedScenarios.has(scenario.scenario_type) 
                                ? scenario.recommendations 
                                : scenario.recommendations.slice(0, 3)
                              ).map((rec, index) => {
                                const transferKey = `${rec.source_pond_id}-${rec.target_pond_id}-${rec.recommended_date}`;
                                const isAccepting = acceptingTransfers.has(transferKey);
                                const isAccepted = acceptedRecommendations.has(transferKey);
                                const isTransferAction = rec.action_type.includes('transfer');
                                
                                return (
                                  <div key={index} className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                                    <div className="flex items-center space-x-3 flex-1">
                                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                        rec.priority === 'high' ? 'bg-red-100 text-red-800' :
                                        rec.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-green-100 text-green-800'
                                      }`}>
                                        {rec.priority}
                                      </span>
                                      <div className="flex-1">
                                        <div className="text-sm font-medium text-gray-900">
                                          {rec.action_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </div>
                                        <div className="text-xs text-gray-600">
                                          {rec.reason.length > 80 ? `${rec.reason.substring(0, 80)}...` : rec.reason}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="flex items-center space-x-3">
                                      <div className="text-right">
                                        <div className="text-sm font-medium text-gray-900">
                                          {rec.recommended_date}
                                        </div>
                                        <div className="text-xs text-gray-600">
                                          {rec.fish_count} {rec.species}
                                        </div>
                                      </div>
                                      {isTransferAction && (
                                        <button
                                          onClick={() => acceptTransfer(rec)}
                                          disabled={isAccepting || isAccepted}
                                          className={`px-3 py-1 text-xs font-medium rounded-full flex items-center space-x-1 ${
                                            isAccepted
                                              ? 'bg-green-200 text-green-900 cursor-default'
                                              : isAccepting 
                                                ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                                                : 'bg-green-100 text-green-800 hover:bg-green-200'
                                          }`}
                                        >
                                          {isAccepted ? (
                                            <>
                                              <CheckIcon className="w-3 h-3" />
                                              <span>Accepted</span>
                                            </>
                                          ) : isAccepting ? (
                                            <>
                                              <ArrowPathIcon className="w-3 h-3 animate-spin" />
                                              <span>Creating...</span>
                                            </>
                                          ) : (
                                            <>
                                              <CheckIcon className="w-3 h-3" />
                                              <span>Accept</span>
                                            </>
                                          )}
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                              {scenario.recommendations.length > 3 && (
                                <div className="text-center">
                                  <button
                                    onClick={() => toggleScenarioExpansion(scenario.scenario_type)}
                                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                  >
                                    {expandedScenarios.has(scenario.scenario_type) 
                                      ? `Show Less` 
                                      : `Show All ${scenario.recommendations.length} Recommendations`
                                    }
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )
              ) : (
                // Traditional Scenarios
                scenarios.length === 0 ? (
                  <div className="text-center py-8">
                    <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Scenarios Available</h3>
                    <p className="text-gray-500">Create grower ponds to see cycle scenarios</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {scenarios.map((scenario) => (
                      <div key={scenario.scenario_type} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-semibold text-gray-900 capitalize">
                            {scenario.scenario_type.replace('-', ' ')}
                          </h4>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            scenario.risk_level === 'low' ? 'bg-green-100 text-green-800' :
                            scenario.risk_level === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {scenario.risk_level} risk
                          </span>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Revenue:</span>
                            <span className="text-sm font-medium text-gray-900">
                              {formatCurrency(scenario.total_revenue)}
                            </span>
                          </div>
                          
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Avg Weight:</span>
                            <span className="text-sm font-medium text-gray-900">
                              {Math.round(scenario.avg_harvest_weight_g)}g
                            </span>
                          </div>
                          
                          <div className="flex justify-between">
                            <span className="text-sm text-gray-600">Seed Needed:</span>
                            <span className="text-sm font-medium text-gray-900">
                              {scenario.seed_requirement.toLocaleString()}
                            </span>
                          </div>
                          
                          <div className="pt-3 border-t border-gray-200">
                            <p className="text-xs text-gray-500">{scenario.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          </div>
        )}

        {activeTab === 'transfers' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Transfer Management</h3>
                <div className="flex space-x-2">
                  <button
                    onClick={fetchTransfers}
                    disabled={loadingTransfers}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50"
                  >
                    {loadingTransfers ? 'Refreshing...' : 'Refresh'}
                  </button>
                  <button
                    onClick={() => setShowCreateTransferModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Create Transfer
                  </button>
                </div>
              </div>
              
              {loadingTransfers ? (
                <div className="text-center py-8">
                  <ArrowPathIcon className="h-8 w-8 text-gray-400 mx-auto mb-4 animate-spin" />
                  <p className="text-gray-500">Loading transfers...</p>
                </div>
              ) : transfers.length === 0 ? (
                <div className="text-center py-8">
                  <ArrowPathIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Transfers Found</h3>
                  <p className="text-gray-500">Create transfers from Cycle Scenarios or manually create new transfers</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {transfers.map((transfer) => (
                    <div key={transfer.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            transfer.status === 'completed' ? 'bg-green-100 text-green-800' :
                            transfer.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {transfer.status}
                          </span>
                          <span className="text-sm font-medium text-gray-900">
                            {transfer.transfer_type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">
                          ID: {transfer.id.slice(0, 8)}...
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <div>
                          <div className="text-xs text-gray-500">From Pond</div>
                          <div className="text-sm font-medium">{getPondName(transfer.source_pond_id)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">To Pond</div>
                          <div className="text-sm font-medium">{getPondName(transfer.target_pond_id)}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Planned Date</div>
                          <div className="text-sm font-medium">{transfer.planned_date}</div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <div>
                          <div className="text-xs text-gray-500">Fish Count</div>
                          <div className="text-sm font-medium">{transfer.fish_count.toLocaleString()}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Species</div>
                          <div className="text-sm font-medium">{transfer.species}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Avg Weight</div>
                          <div className="text-sm font-medium">{transfer.avg_weight_g}g</div>
                        </div>
                      </div>
                      
                      <div className="mb-3">
                        <div className="text-xs text-gray-500">Reason</div>
                        <div className="text-sm text-gray-700">{transfer.reason}</div>
                      </div>
                      
                      {transfer.status === 'pending' && (
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => editTransfer(transfer)}
                            className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-md hover:bg-blue-200 flex items-center space-x-1"
                          >
                            <PencilIcon className="w-4 h-4" />
                            <span>Edit</span>
                          </button>
                          <button
                            onClick={() => completeTransfer(transfer.id)}
                            className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-md hover:bg-green-200 flex items-center space-x-1"
                          >
                            <CheckIcon className="w-4 h-4" />
                            <span>Complete</span>
                          </button>
                          <button
                            onClick={() => cancelTransfer(transfer.id)}
                            className="px-3 py-1 bg-orange-100 text-orange-800 text-sm rounded-md hover:bg-orange-200 flex items-center space-x-1"
                          >
                            <XMarkIcon className="w-4 h-4" />
                            <span>Cancel</span>
                          </button>
                          <button
                            onClick={() => deleteTransfer(transfer.id)}
                            className="px-3 py-1 bg-red-100 text-red-800 text-sm rounded-md hover:bg-red-200 flex items-center space-x-1"
                          >
                            <TrashIcon className="w-4 h-4" />
                            <span>Delete</span>
                          </button>
                        </div>
                      )}
                      
                      {transfer.status === 'completed' && transfer.actual_date && (
                        <div className="text-sm text-gray-600">
                          ✅ Completed on {transfer.actual_date}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Growth Analytics</h3>
              <div className="text-center py-8">
                <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Analytics Dashboard</h3>
                <p className="text-gray-500">Growth analytics and performance metrics will be displayed here</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Manual Transfer Creation Modal */}
      {showCreateTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Create Manual Transfer</h3>
              <button
                onClick={() => {
                  setShowCreateTransferModal(false);
                  setSelectedSourcePond('');
                  setSelectedSpecies('');
                  setSourcePondSpeciesInventory([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              
              try {
                const transferData = {
                  source_pond_id: formData.get('source_pond_id') as string,
                  target_pond_id: formData.get('target_pond_id') as string,
                  source_cycle_id: null,
                  target_cycle_id: null,
                  fish_count: parseInt(formData.get('fish_count') as string),
                  species: formData.get('species') as string,
                  avg_weight_g: parseFloat(formData.get('avg_weight_g') as string),
                  transfer_type: formData.get('transfer_type') as string,
                  planned_date: formData.get('planned_date') as string,
                  reason: formData.get('reason') as string
                };

                const response = await apiClient.post(`/transfers?location_id=${selectedLocation.id}`, transferData);
                
                if (response.status === 200) {
                  alert(`✅ Transfer created successfully!\n\nTransfer ID: ${response.data.id}\nStatus: Pending`);
                  setShowCreateTransferModal(false);
                  fetchTransfers();
                }
              } catch (error) {
                console.error('Error creating transfer:', error);
                alert('❌ Failed to create transfer. Please try again.');
              }
            }}>
              <div className="space-y-4">
                {/* Row 1: From Pond and To Pond */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">From Pond</label>
                    <select
                      name="source_pond_id"
                      value={selectedSourcePond}
                      onChange={(e) => handleSourcePondChange(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select source pond</option>
                      {ponds.map(pond => (
                        <option key={pond.id} value={pond.id}>
                          {pond.name} {pond.type ? `(${pond.type})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To Pond</label>
                    <select
                      name="target_pond_id"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select target pond</option>
                      {ponds.map(pond => (
                        <option key={pond.id} value={pond.id}>
                          {pond.name} {pond.type ? `(${pond.type})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Row 2: Transfer Type and Species */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transfer Type</label>
                    <select
                      name="transfer_type"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select transfer type</option>
                      <option value="nursery_to_grower">Nursery to Grower</option>
                      <option value="grower_to_grower">Grower to Grower</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Species *</label>
                    <select
                      name="species"
                      value={selectedSpecies}
                      onChange={(e) => handleSpeciesChange(e.target.value)}
                      required
                      disabled={!selectedSourcePond || loadingSpeciesInventory}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="">{loadingSpeciesInventory ? 'Loading species...' : 'Select species'}</option>
                      {getNormalizedSpeciesInventory().map(species => (
                        <option key={species.species} value={species.species}>
                          {species.species} ({species.count} available)
                        </option>
                      ))}
                    </select>
                    {selectedSourcePond && !loadingSpeciesInventory && sourcePondSpeciesInventory.length === 0 && (
                      <p className="text-sm text-gray-500 mt-1">No species inventory found in selected pond</p>
                    )}
                  </div>
                </div>

                {/* Row 3: Fish Count and Average Weight */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fish Count *
                      {selectedSpecies && (
                        <span className="text-sm font-normal text-gray-500 ml-2">
                          (Available: {getAvailableCount(selectedSpecies)} fish)
                        </span>
                      )}
                    </label>
                    <input
                      type="number"
                      name="fish_count"
                      required
                      min="1"
                      max={selectedSpecies ? getAvailableCount(selectedSpecies) : undefined}
                      disabled={!selectedSpecies}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder={selectedSpecies ? `Max: ${getAvailableCount(selectedSpecies)}` : 'Select species first'}
                    />
                    {selectedSpecies && getAvailableCount(selectedSpecies) === 0 && (
                      <p className="text-sm text-red-500 mt-1">No fish available for this species</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Average Weight (grams) *</label>
                    <input
                      type="number"
                      name="avg_weight_g"
                      required
                      min="0"
                      step="0.1"
                      value={selectedSpecies ? getAverageWeight(selectedSpecies) : ''}
                      onChange={(e) => {
                        // Allow manual editing
                      }}
                      disabled={!selectedSpecies}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder={selectedSpecies ? `Auto-filled: ${getAverageWeight(selectedSpecies)}g` : 'Select species first'}
                    />
                    {selectedSpecies && (
                      <p className="text-sm text-gray-500 mt-1">
                        Auto-filled from pond inventory • You can edit this value
                      </p>
                    )}
                  </div>
                </div>

                {/* Row 4: Planned Date and Reason (full width) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Planned Date</label>
                    <input
                      type="date"
                      name="planned_date"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                    <textarea
                      name="reason"
                      required
                      rows={3}
                      placeholder="Reason for this transfer..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateTransferModal(false);
                    setSelectedSourcePond('');
                    setSelectedSpecies('');
                    setSourcePondSpeciesInventory([]);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Create Transfer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Transfer Modal */}
      {showEditTransferModal && editingTransfer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Transfer</h3>
              <button
                onClick={() => {
                  setShowEditTransferModal(false);
                  setEditingTransfer(null);
                  setSelectedSourcePond('');
                  setSelectedSpecies('');
                  setSourcePondSpeciesInventory([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={async (e) => {
              e.preventDefault();
              const formData = new FormData(e.target as HTMLFormElement);
              
              try {
                const transferData = {
                  source_pond_id: formData.get('source_pond_id') as string,
                  target_pond_id: formData.get('target_pond_id') as string,
                  source_cycle_id: null,
                  target_cycle_id: null,
                  fish_count: parseInt(formData.get('fish_count') as string),
                  species: formData.get('species') as string,
                  avg_weight_g: parseFloat(formData.get('avg_weight_g') as string),
                  transfer_type: formData.get('transfer_type') as string,
                  planned_date: formData.get('planned_date') as string,
                  reason: formData.get('reason') as string
                };

                const response = await apiClient.put(`/transfers/${editingTransfer.id}`, transferData);
                
                if (response.status === 200) {
                  alert(`✅ Transfer updated successfully!`);
                  setShowEditTransferModal(false);
                  setEditingTransfer(null);
                  setSelectedSourcePond('');
                  setSelectedSpecies('');
                  setSourcePondSpeciesInventory([]);
                  fetchTransfers();
                }
              } catch (error) {
                console.error('Error updating transfer:', error);
                alert('❌ Failed to update transfer. Please try again.');
              }
            }}>
              <div className="space-y-4">
                {/* Row 1: From Pond and To Pond */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">From Pond</label>
                    <select
                      name="source_pond_id"
                      value={selectedSourcePond}
                      onChange={(e) => handleSourcePondChange(e.target.value)}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select source pond</option>
                      {ponds.map(pond => (
                        <option key={pond.id} value={pond.id}>
                          {pond.name} {pond.type ? `(${pond.type})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To Pond</label>
                    <select
                      name="target_pond_id"
                      defaultValue={editingTransfer.target_pond_id}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select target pond</option>
                      {ponds.map(pond => (
                        <option key={pond.id} value={pond.id}>
                          {pond.name} {pond.type ? `(${pond.type})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Row 2: Transfer Type and Species */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Transfer Type</label>
                    <select
                      name="transfer_type"
                      defaultValue={editingTransfer.transfer_type}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select transfer type</option>
                      <option value="nursery_to_grower">Nursery to Grower</option>
                      <option value="grower_to_grower">Grower to Grower</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Species *</label>
                    <select
                      name="species"
                      value={selectedSpecies}
                      onChange={(e) => handleSpeciesChange(e.target.value)}
                      required
                      disabled={!selectedSourcePond || loadingSpeciesInventory}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option value="">{loadingSpeciesInventory ? 'Loading species...' : 'Select species'}</option>
                      {getNormalizedSpeciesInventory().map(species => (
                        <option key={species.species} value={species.species}>
                          {species.species} ({species.count} available)
                        </option>
                      ))}
                    </select>
                    {selectedSourcePond && !loadingSpeciesInventory && sourcePondSpeciesInventory.length === 0 && (
                      <p className="text-sm text-gray-500 mt-1">No species inventory found in selected pond</p>
                    )}
                  </div>
                </div>

                {/* Row 3: Fish Count and Average Weight */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fish Count *
                      {selectedSpecies && (
                        <span className="text-sm font-normal text-gray-500 ml-2">
                          (Available: {getAvailableCount(selectedSpecies)} fish)
                        </span>
                      )}
                    </label>
                    <input
                      type="number"
                      name="fish_count"
                      defaultValue={editingTransfer.fish_count}
                      required
                      min="1"
                      max={selectedSpecies ? getAvailableCount(selectedSpecies) : undefined}
                      disabled={!selectedSpecies}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder={selectedSpecies ? `Max: ${getAvailableCount(selectedSpecies)}` : 'Select species first'}
                    />
                    {selectedSpecies && getAvailableCount(selectedSpecies) === 0 && (
                      <p className="text-sm text-red-500 mt-1">No fish available for this species</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Average Weight (grams) *</label>
                    <input
                      type="number"
                      name="avg_weight_g"
                      defaultValue={editingTransfer.avg_weight_g}
                      required
                      min="0"
                      step="0.1"
                      value={selectedSpecies ? getAverageWeight(selectedSpecies) : editingTransfer.avg_weight_g}
                      onChange={(e) => {
                        // Allow manual editing
                      }}
                      disabled={!selectedSpecies}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder={selectedSpecies ? `Auto-filled: ${getAverageWeight(selectedSpecies)}g` : 'Select species first'}
                    />
                    {selectedSpecies && (
                      <p className="text-sm text-gray-500 mt-1">
                        Auto-filled from pond inventory • You can edit this value
                      </p>
                    )}
                  </div>
                </div>

                {/* Row 4: Planned Date and Reason */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Planned Date</label>
                    <input
                      type="date"
                      name="planned_date"
                      defaultValue={editingTransfer.planned_date}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                    <textarea
                      name="reason"
                      defaultValue={editingTransfer.reason}
                      required
                      rows={3}
                      placeholder="Reason for this transfer..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditTransferModal(false);
                    setEditingTransfer(null);
                    setSelectedSourcePond('');
                    setSelectedSpecies('');
                    setSourcePondSpeciesInventory([]);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Update Transfer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Cycle Form Modal */}
      <CreateCycleForm
        isOpen={showCreateCycle}
        onClose={() => setShowCreateCycle(false)}
        onSuccess={handleCycleCreated}
      />
    </div>
  );
};

export default CyclePlanning;
